import { RotateCw, Droplets, Heart, Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const STEPS = [
  {
    n: "01",
    title: "Insert the cotton",
    desc: "Compress, twist and gently insert one sterile cotton strip into the Mi Binky chamber using your fingertip.",
    img: "/assets/cotton-front.png",
    alt: "Sterile cotton strip for the Mi Binky chamber",
    icon: RotateCw,
  },
  {
    n: "02",
    title: "Prepare the cotton",
    desc: "Apply a few drops of your chosen compatible comfort liquid onto the cotton until lightly moistened — through both holes, exactly as shown in the pack instructions.",
    img: "/assets/usage-demo.webp",
    alt: "Applying comfort drops to the Mi Binky pacifier",
    icon: Droplets,
  },
  {
    n: "03",
    title: "Use Mi Binky",
    desc: "The cotton stays contained within the protected chamber while gentle vapours release naturally through the front ventilation holes as your child uses their Binky.",
    img: "/assets/baby-face.png",
    alt: "Baby using the pink Mi Binky pacifier",
    icon: Heart,
  },
];

const StepCard = ({ step, delay }) => {
  const Icon = step.icon;
  return (
    <Reveal
      delay={delay}
      className="group relative flex h-full flex-col overflow-hidden rounded-[28px] border border-slate-100 bg-white shadow-[0_20px_50px_-24px_rgba(27,79,160,0.25)]"
      data-testid={`how-step-${step.n}`}
    >
      <div className="relative flex h-56 items-center justify-center overflow-hidden bg-gradient-to-br from-binky-pinklight/70 via-white to-sky-50 p-6">
        <span className="absolute left-5 top-3 font-display text-6xl font-extrabold text-binky-blue/10">
          {step.n}
        </span>
        <img
          src={step.img}
          alt={step.alt}
          loading="lazy"
          className="max-h-full w-auto object-contain drop-shadow-xl transition-transform duration-700 group-hover:-rotate-2 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-7">
        <div className="mb-3 flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-binky-pink text-white">
            <Icon className="h-[18px] w-[18px]" />
          </span>
          <h3 className="font-display text-xl font-bold text-slate-900">{step.title}</h3>
        </div>
        <p className="text-sm leading-relaxed text-slate-600 md:text-base">{step.desc}</p>
      </div>
    </Reveal>
  );
};

export const HowItWorks = () => (
  <section
    id="how-it-works"
    data-testid="how-it-works-section"
    className="relative overflow-hidden bg-gradient-to-b from-white via-sky-50/60 to-white py-24 sm:py-32"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>How It Works</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Three gentle steps to comfort.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
          Straight from the Mi Binky instruction card — simple, hygienic and designed for real
          life with little ones.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <StepCard key={s.n} step={s} delay={i * 0.1} />
        ))}
      </div>

      <Reveal className="mt-16 overflow-hidden rounded-[32px] bg-binky-navy shadow-2xl shadow-binky-navy/30" data-testid="replacing-cotton-band">
        <div className="grid md:grid-cols-2">
          <div className="p-10 text-white sm:p-14">
            <Eyebrow className="text-binky-pink">Replacing the cotton</Eyebrow>
            <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
              Out with the used. In with the fresh.
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-white/80 md:text-base">
              Remove the used cotton with the supplied precision tweezers and replace it with a new
              sterile cotton strip whenever required. The used cotton is deliberately removed using
              the tool — never pulled out by hand.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Supplied in every Mi Binky pack",
                "Hygienic, no-touch removal",
                "Fresh strip fitted in seconds",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm font-semibold text-white/90">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-binky-green/20 text-binky-green">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative min-h-[280px] overflow-hidden">
            <img
              src="/assets/binky-pack-red.png"
              alt="Mi Binky pack showing the supplied precision tweezers"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-[50%_82%]"
              data-testid="replacing-cotton-image"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-binky-navy/60 via-transparent to-transparent md:bg-gradient-to-r" />
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-16" data-testid="demo-video-block">
        <div className="mx-auto max-w-4xl">
          <h3 className="mb-6 text-center font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            See how the Mi Binky Comfort System works
          </h3>
          <div className="mx-auto max-w-[420px] overflow-hidden rounded-[32px] border-4 border-white shadow-2xl shadow-binky-blue/20">
            <video
              data-testid="how-it-works-video"
              src="/assets/binky-demo.mp4"
              poster="/assets/binky-demo-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              ref={(el) => {
                if (el) el.muted = true;
              }}
              className="w-full bg-binky-navy"
            />
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-16" data-testid="instruction-card-block">
        <figure className="mx-auto max-w-4xl overflow-hidden rounded-[32px] border border-slate-100 bg-white shadow-xl shadow-binky-blue/10">
          <img
            src="/assets/binky-instructions.png"
            alt="The official Mi Binky instruction card"
            loading="lazy"
            className="w-full object-cover"
            data-testid="instruction-card-image"
          />
          <figcaption className="border-t border-slate-100 px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            The official Mi Binky instruction card — exactly as supplied in every pack
          </figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);
